"use client";

import React, { useEffect, useCallback, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { alpha, Box, Button, Card, CardActionArea, Fade, Icon, Link, Typography } from "@mui/material";
import Arrow2LeftIcon from "@/app/_components/Icons/Arrow2LeftIcon";
import Arrow2RightIcon from "@/app/_components/Icons/Arrow2RightIcon";
import { theme } from "../../../Providers/theme";
import DOMPurify from "isomorphic-dompurify";
import NextLink from "next/link";
import { usePrevNextButtons } from "@/lib/usePrevNextButtons";
import Image from "next/image";

export type ItemType = {
  id: number;
  svg?: string;
  title: string;
  href: string;
  slug: string;
  image?: string | null;
  bgColor?: string | null;
};
const CategoriesSlider = ({ items }: { items?: ItemType[] }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, direction: "rtl", dragFree: true });
  // @ts-ignore
  const { nextBtnDisabled, onNextButtonClick, onPrevButtonClick, prevBtnDisabled } = usePrevNextButtons(emblaApi);
  // useEffect(() => {
  //   if (emblaApi) {
  //     // Embla API is ready
  //   }
  // }, [emblaApi]);
  if (!items || items.length <= 0) {
    return <></>;
  }
  return (
    // embla
    <Box sx={{ py: 1.5, position: "relative", width: "100%" }}>
      {/*  embla__viewport */}
      <Box sx={{ overflow: "hidden", position: "relative" }} ref={emblaRef}>
        {/* embla__container */}
        <Box sx={{ display: "flex", gap: { xs: 1.5, md: 2 } }}>
          {items &&
            items.map((slide, index) => {
              {
                /* embla__slide */
              }
              return (
                <Card
                  elevation={0}
                  sx={{
                    background: "transparent",
                    borderRadius: 3,
                    minWidth: "max-content",
                  }}
                  key={index}
                >
                  <Link
                    component={NextLink}
                    href={`${slide.href}`}
                    underline="none"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      flexDirection: "column",
                      gap: 1,
                      userSelect: "none",
                      "&:hover ": {
                        ".cat-icon": {
                          backgroundColor: slide.bgColor ? alpha(slide.bgColor, 0.7) : "grey.600",
                          color: "white.main",
                        },
                      },
                      "&:active ": {
                        ".cat-icon": {
                          backgroundColor: slide.bgColor ? alpha(slide.bgColor, 0.9) : "grey.900",
                          color: "white.main",
                        },
                      },
                      // "& .MuiTouchRipple-root": {
                      //   color: "secondary.main", // Change ripple color here
                      // },
                    }}
                  >
                    <Box
                      className="cat-icon"
                      sx={{
                        position: "relative",
                        width: { xs: 75, md: 108 },
                        height: { xs: 75, md: 108 },

                        flexShrink: 0,
                        overflow: "hidden",
                        borderRadius: "50%",
                        backgroundColor: slide.bgColor ?? "grey.50",
                        transition: "background-color 0.15s ease-in-out, color 0.15s ease-in-out",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {slide.image ? (
                        <Image
                          src={slide.image}
                          alt={slide.title}
                          fill
                          sizes="(min-width: 900px) 108px, 50px"
                          draggable={false}
                          style={{
                            objectFit: "contain",
                            userSelect: "none",
                            pointerEvents: "none",
                          }}
                        />
                      ) : slide.svg ? (
                        <Icon
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: { xs: "34px !important", md: "45px !important" },
                            height: { xs: "34px !important", md: "45px !important" },
                            "& svg": {
                              width: { xs: "34px !important", md: "45px !important" },
                              height: { xs: "34px !important", md: "45px !important" },
                            },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(slide.svg),
                          }}
                        />
                      ) : null}
                    </Box>

                    <Typography variant={"subtitle1"} component={"h2"} noWrap>
                      {slide.title}
                    </Typography>
                  </Link>
                </Card>
              );
            })}
        </Box>
      </Box>
      <Fade in={!nextBtnDisabled}>
        <Box
          sx={(theme) => ({
            position: "absolute",
            display: "flex",
            alignItems: "center",
            right: 0,
            top: 0,
            height: "100%",
            zIndex: 1,
            background: `linear-gradient(90deg,  ${theme.palette.background.default},transparent)`,
          })}
        >
          <Button
            color="primary"
            variant="contained"
            disableElevation
            sx={{ minWidth: "auto", p: { xs: 0.4, md: 1 }, borderRadius: 50 }}
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          >
            <Arrow2LeftIcon />
          </Button>
        </Box>
      </Fade>
      <Fade in={!prevBtnDisabled}>
        <Box
          sx={(theme) => ({
            position: "absolute",
            display: "flex",
            alignItems: "center",
            left: 0,
            top: 0,
            height: "100%",
            zIndex: 1,
            background: `linear-gradient(90deg, transparent,  ${theme.palette.background.default})`,
          })}
        >
          <Button
            color="primary"
            variant="contained"
            disableElevation
            sx={{ minWidth: "auto", p: { xs: 0.4, md: 1 }, borderRadius: 50 }}
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          >
            <Arrow2RightIcon />
          </Button>
        </Box>
      </Fade>
    </Box>
  );
};

export default CategoriesSlider;
